import React, { FC, ReactElement, SVGAttributes, useCallback } from 'react';

export enum IconVariant {
  GRAPH = 'graph',
  TABLE = 'table',
  STATUS_CHANGE = 'statusChange',
  STATUS_NEW = 'statusNew',
  STATUS_PRICE_REDUCTIONS = 'statusPriceReductions',
  STATUS_PRICE_INCREASE = 'statusPriceIncrease',
  STATUS_SOLD = 'statusSold',
  MARKER = 'marker',
  AGENT = 'agent',
  GALLERY = 'gallery',
  ARROW_DOWN = 'arrowDown',
  GEO_TAG = 'geoTag',
  SEARCH = 'search',
  GEO_TAG_GREY = 'geoTagGrey',
  TICK = 'tick',
  BELL = 'bell',
  BACK = 'back',
  ADD = 'Add',
  EDIT = 'Edit',
  CANCEL = 'Cancel',
}

export interface IconsProps extends SVGAttributes<SVGElement> {
  variant: IconVariant;
}

export const Icons: FC<IconsProps> = (props: IconsProps): ReactElement | null => {
  const { variant, ...rest } = props;

  const renderIcon = useCallback((): ReactElement | null => {
    switch (variant) {
      case IconVariant.CANCEL:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M14.1654 14.1673L5.83203 5.83398"
              stroke="#EC455E"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M5.83463 14.1673L14.168 5.83398"
              stroke="#EC455E"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        );

      case IconVariant.EDIT:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M2 14H5.13144L13.3515 5.77998C13.7667 5.36472 14 4.80152 14 4.21426C14 3.627 13.7667 3.0638 13.3515 2.64854C12.9362 2.23329 12.373 2 11.7857 2C11.1985 2 10.6353 2.23329 10.22 2.64854L2 10.8686V14Z"
              stroke="#747475"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9.4375 3.43164L12.5689 6.56308"
              stroke="#747475"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );

      case IconVariant.ADD:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path d="M6 10H14" stroke="#747475" strokeWidth="2" strokeLinecap="round" />
            <path d="M10 14L10 6" stroke="#747475" strokeWidth="2" strokeLinecap="round" />
          </svg>
        );

      case IconVariant.BACK:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="9"
            height="14"
            viewBox="0 0 9 14"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8.19399 13.6943C8.60199 13.2867 8.60199 12.6259 8.19399 12.2184L2.96989 7L8.19399 1.78165C8.60199 1.37408 8.60199 0.713261 8.19399 0.305681C7.78589 -0.101889 7.12439 -0.101899 6.71639 0.305681L0.80602 6.2096C0.58861 6.4267 0.48704 6.7158 0.50132 7.0002C0.48714 7.2844 0.58871 7.5733 0.80604 7.7904L6.71639 13.6943C7.12439 14.1019 7.78599 14.1019 8.19399 13.6943Z"
              fill="#515151"
            />
          </svg>
        );

      case IconVariant.BELL:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 32 32"
            fill="none"
          >
            <path
              d="M13 24C13 25.6569 14.3431 27 16 27C17.6569 27 19 25.6569 19 24"
              stroke="#04A451"
              strokeWidth="2"
            />
            <path
              d="M14 7.2899L14.2853 8.24835C14.7093 8.12215 15 7.73231 15 7.2899H14ZM9.00007 14H8.00007L8.00007 14L9.00007 14ZM9.00017 18L10.0002 18.0001V18L9.00017 18ZM18 7.28986H17C17 7.73228 17.2907 8.12212 17.7148 8.24832L18 7.28986ZM15 7C15 6.44772 15.4477 6 16 6V4C14.3431 4 13 5.34315 13 7H15ZM15 7.2899V7H13V7.2899H15ZM10.0001 14C10.0001 11.2835 11.8062 8.98619 14.2853 8.24835L13.7147 6.33145C10.4109 7.31474 8.00007 10.3745 8.00007 14H10.0001ZM10.0002 18L10.0001 14L8.00007 14L8.00017 18L10.0002 18ZM8.00007 21C8.29307 21 8.62968 20.9519 8.95155 20.7763C9.2871 20.5933 9.52129 20.3185 9.67567 20.0098C9.94825 19.4647 10.0001 18.7468 10.0002 18.0001L8.00017 17.9999C8.00013 18.352 7.98513 18.6147 7.95847 18.8101C7.93114 19.0104 7.89728 19.0944 7.88686 19.1152C7.88498 19.119 7.91604 19.063 7.99379 19.0206C8.05787 18.9856 8.08201 19 8.00007 19V21ZM8.50003 23C7.98681 23 7.73897 22.8376 7.61216 22.6993C7.46916 22.5433 7.375 22.301 7.375 22C7.375 21.6947 7.4735 21.4104 7.6125 21.225C7.74059 21.0542 7.86987 21 8.00007 21V19C7.13022 19 6.44695 19.4458 6.01252 20.025C5.58901 20.5897 5.375 21.3053 5.375 22C5.375 22.699 5.59336 23.4567 6.13787 24.0507C6.69857 24.6624 7.51325 25 8.50003 25V23ZM23.5 23H8.50003V25H23.5V23ZM24 21C24.1302 21 24.2594 21.0542 24.3875 21.225C24.5265 21.4103 24.625 21.6947 24.625 22C24.625 22.301 24.5308 22.5433 24.3878 22.6993C24.261 22.8376 24.0132 23 23.5 23V25C24.4868 25 25.3015 24.6624 25.8622 24.0507C26.4067 23.4567 26.625 22.699 26.625 22C26.625 21.3053 26.411 20.5897 25.9875 20.025C25.5531 19.4458 24.8699 19 24 19V21ZM22.0001 18C22.0001 18.7466 22.0518 19.4646 22.3243 20.0097C22.4787 20.3185 22.7129 20.5933 23.0485 20.7763C23.3704 20.9519 23.707 21 24 21V19C23.918 19 23.9421 18.9856 24.0062 19.0205C24.084 19.063 24.1151 19.119 24.1132 19.1153C24.1028 19.0945 24.069 19.0105 24.0417 18.8102C24.015 18.6149 24.0001 18.3521 24.0001 18H22.0001ZM22.0001 14V18H24.0001V14H22.0001ZM17.7148 8.24832C20.1939 8.98612 22.0001 11.2834 22.0001 14H24.0001C24.0001 10.3745 21.5891 7.31466 18.2852 6.3314L17.7148 8.24832ZM17 7V7.28986H19V7H17ZM16 6C16.5523 6 17 6.44772 17 7H19C19 5.34315 17.6569 4 16 4V6Z"
              fill="#04A451"
            />
          </svg>
        );

      case IconVariant.TICK:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <g clipPath="url(#clip0_54459_56936)">
              <path
                d="M4 10.0002L8 14.0002L16 6.00018"
                stroke="#04A451"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_54459_56936">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        );

      case IconVariant.GEO_TAG_GREY:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle cx="12" cy="11" r="3" stroke="#AAABAB" strokeWidth="2" />
            <path
              d="M20 11C20 15.6072 15.283 19.9196 13.0931 21.6734C12.4466 22.1912 11.5533 22.1912 10.9069 21.6734C8.71695 19.9196 4 15.6072 4 11C4 6.58172 7.58172 3 12 3C16.4183 3 20 6.58172 20 11Z"
              stroke="#AAABAB"
              strokeWidth="2"
            />
          </svg>
        );

      case IconVariant.SEARCH:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M10.4444 15.8889C13.4513 15.8889 15.8889 13.4513 15.8889 10.4444C15.8889 7.43756 13.4513 5 10.4444 5C7.43756 5 5 7.43756 5 10.4444C5 13.4513 7.43756 15.8889 10.4444 15.8889Z"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M19.0007 19L14.334 14.3333"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );

      case IconVariant.GEO_TAG:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle cx="12" cy="11" r="3" stroke="#515151" strokeWidth="2" />
            <path
              d="M20 11C20 15.6072 15.283 19.9196 13.0931 21.6734C12.4466 22.1912 11.5533 22.1912 10.9069 21.6734C8.71695 19.9196 4 15.6072 4 11C4 6.58172 7.58172 3 12 3C16.4183 3 20 6.58172 20 11Z"
              stroke="#515151"
              strokeWidth="2"
            />
          </svg>
        );

      case IconVariant.ARROW_DOWN:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
          >
            <path
              d="M6.5 10.5L12.5 16.5L18.5 10.5"
              stroke="#262626"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );

      case IconVariant.GRAPH:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            {...rest}
          >
            <path
              d="M19 18H8C6.89543 18 6 17.1046 6 16V5"
              stroke="#515151"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 13L12.3082 9.92241C12.6733 9.43557 13.385 9.38499 13.8153 9.8153L15.1847 11.1847C15.615 11.615 16.3267 11.5644 16.6918 11.0776L19 8"
              stroke="#515151"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );

      case IconVariant.TABLE:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            {...rest}
          >
            <path d="M6 7H5" stroke="#515151" strokeWidth="2" strokeLinecap="round" />
            <path d="M19 7H10" stroke="#515151" strokeWidth="2" strokeLinecap="round" />
            <path d="M6 12H5" stroke="#515151" strokeWidth="2" strokeLinecap="round" />
            <path d="M19 12H10" stroke="#515151" strokeWidth="2" strokeLinecap="round" />
            <path d="M6 17H5" stroke="#515151" strokeWidth="2" strokeLinecap="round" />
            <path d="M19 17H10" stroke="#515151" strokeWidth="2" strokeLinecap="round" />
          </svg>
        );

      case IconVariant.STATUS_CHANGE: {
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            {...rest}
          >
            <path
              d="M6 11.5V4M6 4L3 7M6 4L9 7"
              stroke="#FB913A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14 7.5V15M14 15L11 12M14 15L17 12"
              stroke="#FB913A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      }

      case IconVariant.STATUS_NEW: {
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...rest}
          >
            <path
              d="M13.203 3.77501C13.1477 3.5743 12.8523 3.5743 12.797 3.77501C11.7549 7.55448 10.5545 8.75486 6.77501 9.79696C6.5743 9.85231 6.5743 10.1477 6.77501 10.203C10.5545 11.2451 11.7549 12.4455 12.797 16.225C12.8523 16.4257 13.1477 16.4257 13.203 16.225C14.2451 12.4455 15.4455 11.2451 19.225 10.203C19.4257 10.1477 19.4257 9.85231 19.225 9.79696C15.4455 8.75486 14.2451 7.55448 13.203 3.77501Z"
              fill="#4673D1"
            />
            <path
              d="M4.19967 9.6721C4.1397 9.4801 3.8603 9.4801 3.80033 9.6721C3.17803 11.6645 2.66449 12.178 0.672099 12.8003C0.480099 12.8603 0.480099 13.1397 0.672098 13.1997C2.66449 13.822 3.17803 14.3355 3.80033 16.3279C3.8603 16.5199 4.1397 16.5199 4.19967 16.3279C4.82197 14.3355 5.33551 13.822 7.3279 13.1997C7.5199 13.1397 7.5199 12.8603 7.3279 12.8003C5.33551 12.178 4.82197 11.6645 4.19967 9.6721Z"
              fill="#4673D1"
            />
          </svg>
        );
      }

      case IconVariant.STATUS_PRICE_REDUCTIONS: {
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...rest}
          >
            <path
              d="M4 7L8 11L11 8L17 14M17 14H13M17 14V10"
              stroke="#04A451"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      }

      case IconVariant.STATUS_PRICE_INCREASE: {
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...rest}
          >
            <path
              d="M4 13L8 9L11 12L17 6M17 6H13M17 6V10"
              stroke="#EC455E"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      }

      case IconVariant.STATUS_SOLD: {
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...rest}
          >
            <path
              d="M4.5 14V9C4.5 8.44772 4.94771 8 5.5 8H14.5C15.0523 8 15.5 8.44772 15.5 9V14C15.5 14.5523 15.0523 15 14.5 15H5.5C4.94772 15 4.5 14.5523 4.5 14Z"
              stroke="#928CDA"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <path
              d="M6.5 8L9.24742 4.86009C9.64584 4.40476 10.3542 4.40476 10.7526 4.86009L13.5 8"
              stroke="#928CDA"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <circle cx="10" cy="4" r="2" fill="#928CDA" />
          </svg>
        );
      }

      case IconVariant.MARKER: {
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            {...rest}
          >
            <ellipse cx="8" cy="7.33301" rx="2" ry="2" stroke="#AAABAB" strokeWidth="2" />
            <path
              d="M13.3337 7.33333C13.3337 10.1744 10.6432 12.8472 9.08408 14.1576C8.45004 14.6905 7.55061 14.6905 6.91657 14.1576C5.35748 12.8472 2.66699 10.1744 2.66699 7.33333C2.66699 4.38781 5.05481 2 8.00032 2C10.9458 2 13.3337 4.38781 13.3337 7.33333Z"
              stroke="#AAABAB"
              strokeWidth="2"
            />
          </svg>
        );
      }

      case IconVariant.AGENT:
        return (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...props}
          >
            <circle opacity="0.08" cx="12" cy="12" r="12" fill="#4673D1" />
            <path
              d="M13 9C13 10.6569 11.6569 12 10 12C8.34315 12 7 10.6569 7 9C7 7.34315 8.34315 6 10 6C11.6569 6 13 7.34315 13 9Z"
              stroke="#4673D1"
              strokeWidth="2"
            />
            <path
              d="M14.5 6.04147C15.9189 6.27951 17 7.5135 17 8.99999C17 10.4865 15.9189 11.7205 14.5 11.9585"
              stroke="#4673D1"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M5.5 17.75C5.78045 16.1474 7.06987 14.75 8.49991 14.75H11.5C12.93 14.75 14.2195 16.1474 14.5 17.75"
              stroke="#4673D1"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15.5 14.75C16.93 14.75 18.2195 16.1474 18.5 17.75"
              stroke="#4673D1"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );

      case IconVariant.GALLERY:
        return (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...rest}
          >
            <rect x="6" y="5" width="10" height="10" rx="2" stroke="#262626" strokeWidth="2" />
            <path
              d="M19 9V14.5C19 16.433 17.433 18 15.5 18H10"
              stroke="#262626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );

      default:
        return null;
    }
  }, [variant, rest]);

  return renderIcon();
};
